import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import AgentCard from '../components/AgentCard';
import { fetchAgentGithubData, fetchAgents } from '../services/api';
import { Agent } from '../types/index';
import AddAgentModal from '../components/AddAgentModal';
import { Filter, MessageCircle, Plus } from 'lucide-react';
import FilterModal from '../components/FilterModal';
import Pagination from '../components/Pagination';
import Footer from '../components/Footer';
import AgentCardShimmer from '../components/AgentCardShimmer';
import ChainFilter from '../components/ChainFilter';

interface GitHubData {
    stars: number;
    contributors: number;
    commits: number;
    version: string;
    lastUpdated: string;
    activity: number[];
}


const LandingPage: React.FC = () => {
    const [agents, setAgents] = useState<Agent[]>([]);
    const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);


    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        frameworks: [],
        autonomyLevels: [],
        openSourceStatus: null,
        marketCapMin: '',
        marketCapMax: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [githubData, setGithubData] = useState<Record<string, GitHubData>>({});
    const [selectedChain, setSelectedChain] = useState('all');




    useEffect(() => {
        loadAgents(currentPage);
    }, [currentPage]);

    // Filter agents whenever search term or agents list changes
    useEffect(() => {
        applyFilters(searchTerm, filters);
    }, [searchTerm, filters, agents, selectedChain]);




    const loadGithubData = async (agentId: string) => {
        try {
            const data = await fetchAgentGithubData(agentId);
            setGithubData(prev => ({
                ...prev,
                [agentId]: data
            }));
        } catch (error) {
            console.error(`Error loading GitHub data for agent ${agentId}:`, error);
        }
    };


    const loadAgents = async (page: number) => {
        try {
            setIsLoading(true);
            const response = await fetchAgents(page);
            setAgents(response.agents);
            setFilteredAgents(response.agents);
            setTotalPages(response.pagination.totalPages);

            response.agents.forEach(agent => {
                if (agent.github_url) {
                    loadGithubData(agent.id.toString());
                }
            });


        } catch (err) {
            console.error('Error loading agents:', err);
            setError(err instanceof Error ? err.message : 'Failed to load agents');
            setAgents([]);
            setFilteredAgents([]);
        } finally {
            setIsLoading(false);
        }
    };





    const applyFilters = (searchTerm: string, filters: any) => {
        let filtered = [...agents];

        // Apply search filter
        if (searchTerm) {
            const searchValue = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(agent => {
                return (
                    (agent.name?.toLowerCase().includes(searchValue) || false) ||
                    (agent.description?.toLowerCase().includes(searchValue) || false) ||
                    (agent.framework?.toLowerCase().includes(searchValue) || false) ||
                    (Array.isArray(agent.tags) && agent.tags.some(tag =>
                        tag?.toLowerCase().includes(searchValue)
                    ))
                );
            });
        }

        // Apply chain filter
        if (selectedChain !== 'all') {
            filtered = filtered.filter(agent => {
                // Convert both to lowercase for case-insensitive comparison
                const agentChains = agent.chains?.map(chain => chain.toLowerCase()) || [];
                return agentChains.includes(selectedChain.toLowerCase());
            });
        }

        // Apply other filters
        if (filters.frameworks.length > 0) {
            filtered = filtered.filter(agent =>
                filters.frameworks.includes(agent.framework)
            );
        }

        // Apply autonomy level filter
        if (filters.autonomyLevels.length > 0) {
            filtered = filtered.filter(agent =>
                filters.autonomyLevels.includes(agent.level)
            );
        }

        // Apply open source filter
        if (filters.openSourceStatus !== null) {
            filtered = filtered.filter(agent =>
                agent.openSource === filters.openSourceStatus
            );
        }

        // Apply market cap filter
        if (filters.marketCapMin || filters.marketCapMax) {
            filtered = filtered.filter(agent => {
                const marketCap = parseFloat(agent.marketCap.toString().replace(/[^0-9.]/g, ''));
                const min = filters.marketCapMin ? parseFloat(filters.marketCapMin) : 0;
                const max = filters.marketCapMax ? parseFloat(filters.marketCapMax) : Infinity;
                return marketCap >= min && marketCap <= max;
            });
        }

        setFilteredAgents(filtered);
    };
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAddAgent = (newAgent: Agent) => {
        setAgents(prevAgents => [...prevAgents, newAgent]);
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Reset to first page when filters or search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filters]);

    return (
        <div className="min-h-screen bg-black relative overflow-hidden">
            {/* Background gradient */}
            {/* <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-[#0a0a0a]" /> */}
            <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-purple-900/20 to-[#000000]" />
            <main className="container mx-auto px-4 md:px-8 py-2 md:py-8 relative z-10">
                <Navbar />

                {/* Hero Section */}
                <div className="text-center mb-8 md:mb-12">

                    {/* <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6 md:mb-10">AI Agents</h2> */}


                    <h1 className="text-5xl md:text-6xl  font-bold text-transparent  bg-clip-text bg-gradient-to-r from-purple-500 to-pink-600 mt-2 md:mt-[-80px] pb-12 mb-2">
                        AgentPulse
                    </h1>

                    {/* Pulse Animation */}
                    <div className="relative flex justify-center items-center w-full">
                        <div className="relative flex justify-center align-middle w-full max-w-2xl h-32 mt-[-80px] mb-[-18px]">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent relative">
                                    <div className="absolute inset-0 animate-pulse-wave" />
                                </div>
                            </div>

                            {/* Pulse dot */}
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                <div className="w-4 h-4 bg-purple-500 rounded-full animate-ping" />
                                <div className="absolute inset-0 w-4 h-4 bg-purple-500 rounded-full animate-pulse" />
                            </div>
                        </div>
                    </div>


                    <p className="text-gray-400 text-md md:text-xl mb-6 md:mb-10">
                        A curated list of AI agents, research, analyze and get the most relevant info
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-xl mx-auto mb-6 md:mb-6 px-4">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder="Search agents..."
                            className="w-full px-4 md:px-6 py-2 md:py-2.5 rounded-full bg-transparent text-white border border-white focus:outline-solid focus:border-gray-500"
                        />
                    </div>
                </div>

                {/* AI Agents Index */}
                {/* Near your AI Agent Index heading */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className='text-white text-xl md:text-2xl'>AI Agent Index</h2>
                    <button
                        onClick={() => setIsFilterModalOpen(true)}
                        className="flex items-center gap-2 px-3 md:px-4 py-1 md:py-0  text-gray-300 rounded-full hover:underline transition-colors"
                    >
                        <Filter className="w-4 h-4" />
                        Filters
                        {(filters.frameworks.length > 0 ||
                            filters.autonomyLevels.length > 0 ||
                            filters.openSourceStatus !== null ||
                            filters.marketCapMin ||
                            filters.marketCapMax) && (
                                <span className="ml-2 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
                                    Active
                                </span>
                            )}
                    </button>
                </div>

                <FilterModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    filters={filters}
                    onFilterChange={setFilters}
                    agents={agents}
                />

                <ChainFilter 
                    selectedChain={selectedChain}
                    onChainSelect={(chain) => {
                        setSelectedChain(chain);
                        // Optionally scroll to top when changing chains
                        // window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                />

                <div className="border border-gray-900 rounded-xl p-4 md:p-6 ">
                    {/* Table Headers - Hide on mobile */}
                    <div className="hidden md:grid grid-cols-7 gap-6 text-sm text-gray-400 mb-4 px-6 pb-3 border-b border-gray-800">
                        <div className="col-span-2">Name</div>
                        <div>Level of Autonomy</div>
                        <div>Market Cap</div>
                        <div>Token Utility</div>
                        <div>Github Activity</div>
                        <div>Framework</div>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 mb-4">
                            {error}
                        </div>
                    )}

                    {isLoading ? (
                        <AgentCardShimmer />
                        // <div className="text-white p-4">Loading agents...</div>

                    ) : filteredAgents.length === 0 ? (
                        <div className="text-gray-400 p-4">
                            {searchTerm ? 'No agents found matching your search' : 'No agents found'}
                        </div>
                    ) : (
                        <>
                            <div className="space-y-4">
                                {filteredAgents.map((agent) => (
                                    <AgentCard
                                        key={agent.id}
                                        {...agent}
                                        website_url={agent.website_url}
                                        github_url={agent.github_url}
                                        documentation_url={agent.documentation_url}
                                        x_url={agent.x_url}
                                        discord_url={agent.discord_url}
                                        telegram_url={agent.telegram_url}
                                        cookie_url={agent.cookie_url}
                                        agent_team_status={agent.agent_team_status}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </main>

            <Footer />

            <AddAgentModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleAddAgent}
            />
        </div>
    );
};

export default LandingPage; 